/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 *
 * Enables drag-to-scroll and keyboard navigation on any element with the `.drag-scroll` class.
 * Touch scrolling is handled natively by the browser — mouse drag only targets
 * desktop devices with (hover: hover) and (pointer: fine).
 *
 * Keyboard (ArrowLeft/Right, Home/End) activates when the scroll container itself is focused
 * (e.g. via tabindex="0"). Keys are not intercepted when focus is inside a child element.
 */

const DRAG_THRESHOLD = 5;
const INTERACTIVE_SELECTOR = 'button, input, select, textarea, label, a';
const NAVIGATION_KEYS = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];

/**
 * Manages drag-to-scroll and keyboard scroll behaviour via delegated listeners.
 * Move/up listeners are only attached while a drag is active.
 */
class DragScrollController {
  /** @type {DragScrollController | null} */
  static #instance = null;

  static init() {
    if (this.#instance) return;
    this.#instance = new DragScrollController();
  }

  constructor() {
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onClick);

    if (matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mousedown', this.#onMouseDown);
    }
  }

  /**
   * @param {HTMLElement} el
   * @returns {ScrollBehavior}
   */
  #scrollBehavior(el) {
    const computed = getComputedStyle(el).scrollBehavior;
    if (computed === 'auto') return 'auto';
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return 'instant';
    return 'smooth';
  }

  /**
   * @param {HTMLElement} el
   * @returns {HTMLElement | null}
   */
  #focusedScroller(el) {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return null;
    if (active !== el || el.scrollWidth <= el.clientWidth) return null;
    return el;
  }

  /**
   * Scrolls by one viewport page. Works with scroll-snap carousels and generic lists.
   *
   * @param {HTMLElement} el
   * @param {number} direction
   * @param {ScrollBehavior} behavior
   */
  #scrollPage(el, direction, behavior) {
    const pageWidth = el.clientWidth;
    if (pageWidth === 0) return;

    const currentPage = Math.round(el.scrollLeft / pageWidth);
    const maxPage = Math.max(0, Math.ceil(el.scrollWidth / pageWidth) - 1);
    const nextPage = Math.max(0, Math.min(currentPage + direction, maxPage));

    el.scrollTo({ left: nextPage * pageWidth, behavior });
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (!NAVIGATION_KEYS.includes(event.key)) return;

    const active = document.activeElement;
    if (!(active instanceof HTMLElement)) return;

    const el = active.closest('.drag-scroll');
    if (!(el instanceof HTMLElement)) return;

    const scroller = this.#focusedScroller(el);
    if (!scroller) return;

    const behavior = this.#scrollBehavior(scroller);

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.#scrollPage(scroller, -1, behavior);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#scrollPage(scroller, 1, behavior);
        break;
      case 'Home':
        event.preventDefault();
        scroller.scrollTo({ left: 0, behavior });
        break;
      case 'End':
        event.preventDefault();
        scroller.scrollTo({ left: scroller.scrollWidth - scroller.clientWidth, behavior });
        break;
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const el = /** @type {Element} */ (event.target)?.closest('.drag-scroll');
    if (!(el instanceof HTMLElement)) return;
    if (el.scrollWidth <= el.clientWidth) return;
    if (/** @type {Element} */ (event.target).closest(INTERACTIVE_SELECTOR)) return;
    if (document.activeElement === el) return;

    el.focus({ preventScroll: true });
  };

  /** @param {MouseEvent} event */
  #onMouseDown = (event) => {
    const el = /** @type {Element} */ (event.target)?.closest('.drag-scroll');
    if (!(el instanceof HTMLElement)) return;
    if (event.button !== 0 || el.scrollWidth <= el.clientWidth) return;
    if (/** @type {Element} */ (event.target).closest(INTERACTIVE_SELECTOR)) return;

    el.focus({ preventScroll: true });

    event.preventDefault();

    const startX = event.pageX;
    const startScroll = el.scrollLeft;
    let moved = false;

    el.classList.add('is-dragging');

    /** @param {MouseEvent} e */
    const onMove = (e) => {
      const delta = startX - e.pageX;
      if (Math.abs(delta) > DRAG_THRESHOLD) moved = true;
      el.scrollLeft = startScroll + delta;
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      el.classList.remove('is-dragging');

      if (moved) {
        el.addEventListener(
          'click',
          (e) => {
            e.preventDefault();
            e.stopPropagation();
          },
          { once: true, capture: true },
        );
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };
}

DragScrollController.init();
